import { reactive, ref } from "vue";

let show = ref(false);
let requestToken = 0;

const dismiss = () => {
  confirmation.show = false;
  confirmation.loading = false;
};

// Cancelling is always synchronous relative to the currently open dialog, so it can dismiss
// unconditionally. Confirming can be async (onOk may return a promise); in that case we keep
// the dialog open with a loading state instead of closing it right away. If onOk itself opens
// a *new* confirmation (e.g. a follow-up "are you sure" for a 409), `token` stops the original
// call's delayed dismiss() from clobbering that new dialog once the promise settles.
const handleCancel = (callback = null) => {
  dismiss();
  if (callback) callback();
};

const handleOk = async (callback = null, token) => {
  if (!callback) {
    if (confirmation.token === token) dismiss();
    return;
  }

  const result = callback();

  if (result && typeof result.then === "function") {
    if (confirmation.token === token) confirmation.loading = true;
    try {
      await result;
    } finally {
      if (confirmation.token === token) dismiss();
    }
  } else if (confirmation.token === token) {
    dismiss();
  }
};

const confirmation = reactive({
  show: false,
  loading: false,
  token: 0,
  element: "body",
  title: "Confirmation Title",
  message: "Confirmation Message",
  okLabel: "Confirm",
  cancelLabel: "Cancel",
  showCancelButton: true,
  handleOk: () => handleOk(),
  handleCancel: () => handleCancel(),
});

export default function useConfirmation() {
  const confirm = ({
    title,
    message,
    okLabel,
    cancelLabel,
    onOk = null,
    onCancel = null,
    element = null,
    showCancelButton = true,
    okButtonClass = "bg-primary-500 hover:bg-primary-600 text-white",
    cancelButtonClass = "bg-white text-slate-900 hover:bg-slate-50 ring-1 ring-inset ring-slate-300",
  }) => {
    if (confirmation.show) {
      confirmation.show = false;
    }
    requestToken += 1;
    const token = requestToken;
    confirmation.token = token;
    confirmation.loading = false;
    confirmation.title = title || confirmation.title;
    confirmation.message = message || confirmation.message;
    confirmation.okLabel = okLabel || confirmation.okLabel;
    confirmation.cancelLabel = cancelLabel || confirmation.cancelLabel;
    confirmation.showCancelButton = showCancelButton;
    confirmation.handleOk = () => handleOk(onOk, token);
    confirmation.handleCancel = () => handleCancel(onCancel);
    confirmation.show = true;
    confirmation.element = element || confirmation.element;
    confirmation.okButtonClass = okButtonClass;
    confirmation.cancelButtonClass = cancelButtonClass;
  };

  return {
    dismiss,
    confirm,
    confirmation,
    show,
  };
}
